// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

import React from "react";
import { FormattedMessage } from "react-intl";

import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import Select from "react-select";

import { reactSelectDarkStyles } from "../../styles/reactSelectDark";
import { getString, localizeEvent } from "../../logic/strings";
import { RegistrationFilters as RegistrationFiltersState } from "../../logic/registration/filterRegistrations";
import { Equipment, Event, Language, Sex } from "../../types/dataTypes";
import { MeetState } from "../../types/stateTypes";
import { checkExhausted } from "../../types/utils";

type SelectOption<T extends string> = { value: T; label: string };

interface Props {
  filters: RegistrationFiltersState;
  meet: MeetState;
  language: Language;
  visibleCount: number;
  totalCount: number;
  onChange: (filters: RegistrationFiltersState) => void;
  onClear: () => void;
}

const extractValues = <T extends string>(value: unknown): T[] => {
  if (value instanceof Array) {
    return value.map((option) => option.value);
  }
  if (value === null) {
    return [];
  }
  return [];
};

const toSelectedOptions = <T extends string>(
  values: ReadonlyArray<T>,
  labelFor: (value: T) => string,
): SelectOption<T>[] => {
  return values.map((value) => ({ value, label: labelFor(value) }));
};

const RegistrationFilters: React.FC<Props> = (props) => {
  const { filters, meet, language } = props;
  const stringSelectPlaceholder = getString("common.select-placeholder", language);

  const sexOptions: SelectOption<Sex>[] = [
    { value: "M", label: getString("sex.m", language) },
    { value: "F", label: getString("sex.f", language) },
    { value: "Mx", label: getString("sex.mx", language) },
  ];

  const equipmentOptions: SelectOption<Equipment>[] = [
    { value: "Bare", label: getString("equipment.bare", language) },
    { value: "Sleeves", label: getString("equipment.sleeves", language) },
    { value: "Wraps", label: getString("equipment.wraps", language) },
    { value: "Single-ply", label: getString("equipment.single-ply", language) },
    { value: "Multi-ply", label: getString("equipment.multi-ply", language) },
    { value: "Unlimited", label: getString("equipment.unlimited", language) },
  ];

  const divisionOptions: SelectOption<string>[] = meet.divisions.map((division) => ({
    value: division,
    label: division,
  }));

  const eventOptions: SelectOption<Event>[] = [
    { value: "S", label: getString("event.s", language) },
    { value: "B", label: getString("event.b", language) },
    { value: "D", label: getString("event.d", language) },
    { value: "BD", label: getString("event.bd", language) },
    { value: "SBD", label: getString("event.sbd", language) },
    { value: "SB", label: getString("event.sb", language) },
    { value: "SD", label: getString("event.sd", language) },
  ];

  const labelForSex = (sex: Sex): string => {
    switch (sex) {
      case "M":
        return getString("sex.m", language);
      case "F":
        return getString("sex.f", language);
      case "Mx":
        return getString("sex.mx", language);
      default:
        checkExhausted(sex);
        return "";
    }
  };

  const labelForEquipment = (equipment: Equipment): string => {
    switch (equipment) {
      case "Bare":
        return getString("equipment.bare", language);
      case "Sleeves":
        return getString("equipment.sleeves", language);
      case "Wraps":
        return getString("equipment.wraps", language);
      case "Single-ply":
        return getString("equipment.single-ply", language);
      case "Multi-ply":
        return getString("equipment.multi-ply", language);
      case "Unlimited":
        return getString("equipment.unlimited", language);
      default:
        checkExhausted(equipment);
        return "";
    }
  };

  const hasActiveFilters =
    filters.sexes.length > 0 ||
    filters.equipment.length > 0 ||
    filters.divisions.length > 0 ||
    filters.events.length > 0;

  const gridStyle = { padding: "0px", margin: "0px" };

  return (
    <Card style={{ marginBottom: "17px", overflow: "visible" }}>
      <Card.Header>
        <FormattedMessage id="registration.filters-header" defaultMessage="Filters" />
      </Card.Header>
      <Card.Body>
        <Container style={gridStyle}>
          <Row>
            <Col md={2}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id="registration.sex-label" defaultMessage="Sex" />
                </Form.Label>
                <Select
                  menuPlacement="auto"
                  placeholder={stringSelectPlaceholder}
                  options={sexOptions}
                  isClearable={true}
                  isMulti={true}
                  onChange={(value) =>
                    props.onChange({
                      ...filters,
                      sexes: extractValues<Sex>(value),
                    })
                  }
                  value={toSelectedOptions(filters.sexes, labelForSex)}
                  styles={reactSelectDarkStyles}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id="registration.equipment-label" defaultMessage="Equipment" />
                </Form.Label>
                <Select
                  menuPlacement="auto"
                  placeholder={stringSelectPlaceholder}
                  options={equipmentOptions}
                  isClearable={true}
                  isMulti={true}
                  onChange={(value) =>
                    props.onChange({
                      ...filters,
                      equipment: extractValues<Equipment>(value),
                    })
                  }
                  value={toSelectedOptions(filters.equipment, labelForEquipment)}
                  styles={reactSelectDarkStyles}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id="registration.divisions-label" defaultMessage="Divisions" />
                </Form.Label>
                <Select
                  menuPlacement="auto"
                  placeholder={stringSelectPlaceholder}
                  options={divisionOptions}
                  isClearable={true}
                  isMulti={true}
                  onChange={(value) =>
                    props.onChange({
                      ...filters,
                      divisions: extractValues<string>(value),
                    })
                  }
                  value={toSelectedOptions(filters.divisions, (division) => division)}
                  styles={reactSelectDarkStyles}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id="registration.events-label" defaultMessage="Events" />
                </Form.Label>
                <Select
                  menuPlacement="auto"
                  placeholder={stringSelectPlaceholder}
                  options={eventOptions}
                  isClearable={true}
                  isMulti={true}
                  onChange={(value) =>
                    props.onChange({
                      ...filters,
                      events: extractValues<Event>(value),
                    })
                  }
                  value={toSelectedOptions(filters.events, (event) => localizeEvent(event, language))}
                  styles={reactSelectDarkStyles}
                />
              </Form.Group>
            </Col>
          </Row>
        </Container>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
          <span>
            <FormattedMessage
              id="registration.filters-count"
              defaultMessage="{visible} of {total} lifters"
              values={{ visible: props.visibleCount, total: props.totalCount }}
            />
          </span>
          <Button variant="secondary" onClick={props.onClear} disabled={!hasActiveFilters}>
            <FormattedMessage id="registration.button-clear-filters" defaultMessage="Clear Filters" />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default RegistrationFilters;
